import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../components/LucideIcon";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { hostname, parseError, User } from "../../utils";
import CampaignFormDialog, { Campaign } from "./dialogs/CampaignFormDialog";
import CampaignDetailDialog from "./dialogs/CampaignDetailDialog";
import ApplyTemplateDialog from "./dialogs/ApplyTemplateDialog";
import MarketingCampaignsMetricsBar from "./MarketingCampaignsMetricsBar";
import MarketingCampaignTemplates from "./MarketingCampaignTemplates";
import ProgressBar from "../../components/ProgressBar";
import {
  MARKETING_CAMPAIGN_STATUS_DRAFT,
  MARKETING_CAMPAIGN_STATUS_SCHEDULED,
  MARKETING_CAMPAIGN_STATUS_SENDING,
  MARKETING_CAMPAIGN_STATUS_SENT,
  MARKETING_CAMPAIGN_STATUS_FAILED,
  MARKETING_CAMPAIGN_STATUS_CANCELLED,
} from "../../constants";

interface MarketingCampaignsProps {
  currentUser: User | any;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
}

const STATUS_COLORS: Record<string, "default" | "primary" | "warning" | "error"> = {
  [MARKETING_CAMPAIGN_STATUS_DRAFT]: "default",
  [MARKETING_CAMPAIGN_STATUS_SCHEDULED]: "primary",
  [MARKETING_CAMPAIGN_STATUS_SENDING]: "warning",
  [MARKETING_CAMPAIGN_STATUS_SENT]: "primary",
  [MARKETING_CAMPAIGN_STATUS_FAILED]: "error",
  [MARKETING_CAMPAIGN_STATUS_CANCELLED]: "default",
};

const STATUS_LABELS: Record<string, string> = {
  [MARKETING_CAMPAIGN_STATUS_DRAFT]: "Draft",
  [MARKETING_CAMPAIGN_STATUS_SCHEDULED]: "Scheduled",
  [MARKETING_CAMPAIGN_STATUS_SENDING]: "Sending",
  [MARKETING_CAMPAIGN_STATUS_SENT]: "Sent",
  [MARKETING_CAMPAIGN_STATUS_FAILED]: "Failed",
  [MARKETING_CAMPAIGN_STATUS_CANCELLED]: "Cancelled",
};

const MarketingCampaigns: React.FC<MarketingCampaignsProps> = ({
  companyId,
  getToken,
  eventListener,
  openDialog,
}) => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Partial<Campaign> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "templates" ? "templates" : "campaigns";
  const [tab, setTab] = useState<"campaigns" | "templates">(initialTab);

  useEffect(() => {
    const qTab = searchParams.get("tab");
    const nextTab = qTab === "templates" ? "templates" : "campaigns";
    if (nextTab !== tab) setTab(nextTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, v: "campaigns" | "templates") => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "templates") {
      next.set("tab", "templates");
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyPresetId, setApplyPresetId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCampaign, setMenuCampaign] = useState<Campaign | null>(null);

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, campaign: Campaign) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuCampaign(campaign);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuCampaign(null);
  };

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${hostname}/marketing-campaigns`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(res.data.items || []);
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setLoading(false);
    }
  }, [companyId, getToken, eventListener]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleNew = () => {
    setEditingCampaign(null);
    setFormOpen(true);
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setFormOpen(true);
  };

  const handleViewDetails = (campaign: Campaign) => {
    setDetailCampaign(campaign);
    setDetailOpen(true);
  };

  const handleDelete = (campaign: Campaign) => {
    openDialog(
      "Delete Campaign",
      `Are you sure you want to delete campaign "${campaign.name}"?`,
      "Delete",
      async () => {
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/marketing-campaigns/${campaign.id}`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          });
          loadCampaigns();
        } catch (err) {
          eventListener({ type: "SET_ERROR", error: parseError(err) });
        }
      }
    );
  };

  const handleSendNow = (campaign: Campaign) => {
    openDialog(
      "Send Campaign",
      `Send campaign "${campaign.name}" to the attached list now?`,
      "Send",
      async () => {
        try {
          const token = await getToken();
          await axios.post(
            `${hostname}/marketing-campaigns/${campaign.id}/send-now`,
            { companyId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          loadCampaigns();
        } catch (err) {
          eventListener({ type: "SET_ERROR", error: parseError(err) });
        }
      }
    );
  };

  const handleCancel = (campaign: Campaign) => {
    openDialog(
      "Cancel Campaign",
      `Cancel scheduled campaign "${campaign.name}"?`,
      "Confirm",
      async () => {
        try {
          const token = await getToken();
          await axios.post(
            `${hostname}/marketing-campaigns/${campaign.id}/cancel`,
            { companyId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          loadCampaigns();
        } catch (err) {
          eventListener({ type: "SET_ERROR", error: parseError(err) });
        }
      }
    );
  };

  const handleOpenApply = (templateId?: string | null) => {
    setApplyPresetId(templateId || null);
    setApplyOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <MarketingCampaignsMetricsBar companyId={companyId} getToken={getToken} />

      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mt: 2, mb: 2 }}
      >
        <Tab label="Campaigns" value="campaigns" />
        <Tab label="Templates" value="templates" />
      </Tabs>

      {tab === "campaigns" && (
        <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Build a marketing email, attach a smart list, and schedule the send.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadCampaigns} disabled={loading}>
              <LucideIcon name="RefreshCw" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<LucideIcon name="Plus" size={18} />}
            onClick={() => handleOpenApply(null)}
          >
            From Template
          </Button>
          <Button variant="contained" startIcon={<LucideIcon name="Plus" size={18} />} onClick={handleNew}>
            New Campaign
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No campaigns yet. Click "New Campaign" to build your first one.
        </Alert>
      ) : (
        <Paper sx={{ mt: 2 }}>
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>List</TableCell>
                  <TableCell>Sender</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Progress</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  onClick={() => handleViewDetails(c)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.subject}</TableCell>
                  <TableCell>{c.smartList?.name || "—"}</TableCell>
                  <TableCell>
                    {c.senderProfile?.name || c.senderProfile?.email || "—"}
                  </TableCell>
                  <TableCell>
                    {c.scheduleType === "scheduled" && c.scheduledAt
                      ? new Date(c.scheduledAt).toLocaleString()
                      : "Immediate"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[c.status] || c.status}
                      color={STATUS_COLORS[c.status] || "default"}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {(() => {
                      const sent = c.sentCount || 0;
                      const failed = c.failedCount || 0;
                      const skipped = c.skippedCount || 0;
                      const total = c.totalRecipients || 0;
                      const completed = sent + failed + skipped;
                      return (
                        <Box>
                          <ProgressBar
                            value={total > 0 ? (completed / total) * 100 : 0}
                            total={total}
                            completed={completed}
                            showLabel={false}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {sent} / {total} sent
                            {failed > 0 && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="error"
                                sx={{ ml: 1 }}
                              >
                                {failed} failed
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={(e) => openRowMenu(e, c)}>
                      <LucideIcon name="EllipsisVertical" size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuCampaign) handleViewDetails(menuCampaign);
            closeRowMenu();
          }}
        >
          View details
        </MenuItem>
        {menuCampaign &&
          (menuCampaign.status === MARKETING_CAMPAIGN_STATUS_DRAFT ||
            menuCampaign.status === MARKETING_CAMPAIGN_STATUS_FAILED ||
            menuCampaign.status === MARKETING_CAMPAIGN_STATUS_CANCELLED) && (
            <MenuItem
              onClick={() => {
                if (menuCampaign) handleSendNow(menuCampaign);
                closeRowMenu();
              }}
            >
              Send now
            </MenuItem>
          )}
        {menuCampaign &&
          menuCampaign.status === MARKETING_CAMPAIGN_STATUS_SCHEDULED && (
            <MenuItem
              onClick={() => {
                if (menuCampaign) handleCancel(menuCampaign);
                closeRowMenu();
              }}
            >
              Cancel schedule
            </MenuItem>
          )}
        <MenuItem
          onClick={() => {
            if (menuCampaign) handleEdit(menuCampaign);
            closeRowMenu();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuCampaign) handleDelete(menuCampaign);
            closeRowMenu();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

        </>
      )}

      {tab === "templates" && (
        <MarketingCampaignTemplates
          companyId={companyId}
          getToken={getToken}
          eventListener={eventListener}
          openDialog={openDialog}
          onApplyTemplate={(templateId) => {
            setTab("campaigns");
            handleOpenApply(templateId);
          }}
        />
      )}

      <CampaignFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        companyId={companyId}
        getToken={getToken}
        campaign={editingCampaign}
        openDialog={openDialog}
        onSaved={() => {
          setFormOpen(false);
          loadCampaigns();
        }}
        onError={(err) => eventListener({ type: "SET_ERROR", error: err })}
      />

      <ApplyTemplateDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        companyId={companyId}
        getToken={getToken}
        presetTemplateId={applyPresetId}
        onApply={(initial) => {
          setApplyOpen(false);
          setEditingCampaign(initial);
          setFormOpen(true);
        }}
        onError={(err) => eventListener({ type: "SET_ERROR", error: err })}
      />

      <CampaignDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        campaign={detailCampaign}
        companyId={companyId}
        getToken={getToken}
        onError={(err) => eventListener({ type: "SET_ERROR", error: err })}
      />
    </Box>
  );
};

export default MarketingCampaigns;
